import { axios, AxiosError } from '@ori/axios';
import { isNonNullable } from '@ori/ts-utils';
import { DEFAULT_TIMEOUT } from '../constants';
import { EmptyBodyFetchGraphqlError } from '../errors/EmptyBodyFetchGraphqlError';
import { FetchGraphqlError } from '../errors/FetchGraphqlError';
import { NonOkResponseFetchGraphqlError } from '../errors/NonOkResponseFetchGraphqlError';
import { handleResponse } from './handleResponse';
import { isEmptyObject } from './isEmptyObject';
/**
 * Fetches data from a GraphQL server using axios library.
 *
 * @typeParam Data The expected data type returned from the GraphQL server.
 * @typeParam Variables The type of variables that the GraphQL query takes.
 * @param options Request options object.
 * @returns A promise that resolves to the requested data or rejects with an error.
 * @throws Error When unknown error happens
 * @throws {FetchGraphqlError} When unknown graphql error happens
 * @throws {EmptyBodyFetchGraphqlError} When graphql server ends request with empty response
 * @throws {NonOkResponseFetchGraphqlError} When graphql server ends request with anything than 200 HTTP status code
 */
export async function fetchGraphql({
  query,
  variables,
  url,
  headers,
  timeout = DEFAULT_TIMEOUT,
  requestConfig = {},
  // eslint-disable-next-line no-console -- default error handler
  onError = error => console.error(error)
}) {
  const requestData = {
    query,
    ...(variables && !isEmptyObject(variables) && {
      variables
    })
  };
  try {
    const response = await axios.post('/graphql', requestData, {
      ...requestConfig,
      baseURL: new URL(url).origin,
      headers,
      timeout
    });
    return handleResponse(response, {
      onSuccess: ({
        data: {
          data
        }
      }) => {
        if (isNonNullable(data)) {
          return data;
        }
        throw new EmptyBodyFetchGraphqlError(response, query);
      },
      onError: () => {
        throw new NonOkResponseFetchGraphqlError(response, query);
      }
    });
  } catch (error_) {
    const error = error_ instanceof Error ? error_ : new Error(String(error_));
    let rethrowError = error;

    // We need to parse that error as it would be unreadable in Application Inisights this way
    // And also would include too much info
    if (error instanceof AxiosError) {
      rethrowError = new FetchGraphqlError(error, query);
    }
    onError(rethrowError);
    throw rethrowError;
  }
}
//# sourceMappingURL=fetchGraphql.js.map